import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useThemePalette } from "common/hooks/theme_palette";
import CActions from "components/Molecules/Crud/components/actions";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
  createVariantBranchRequest,
  createVariantRequest,
  deleteProductVariantRequest,
  deleteVariantBranchRequest,
  getOptionValuesRequest,
  getProductVariantsRequest,
  getVariantDetailsRequest,
  getVariantsBranchesRequest,
  updateVariantBranchRequest,
  updateVariantRequest,
} from "services/modules/products";

import ProductVariantTemplate from "templates/products/productVariant";

export default function ProductVariantsPage({
  isHeaderChildren = true,
  productId,
  optionTypes,
  refOpenVriantForm,
  storeId,
  variantPermissions = {},
  isSuperAdmin = false,
  tempVariants = [],
  setTempVariants,
}) {
  const { t, i18n } = useTranslation();
  const { orange, oliveGreen } = useThemePalette();
  const dispatch = useDispatch();
  const anchorRef = useRef(null);

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id") ?? productId;
  const type = searchParams.get("type");
  const isCreationMode = !id;

  const [locations, setLocations] = useState([]);
  const [isOpenForm, setIsOpenForm] = useState(false);
  const [isOpenDetails, setIsOpenDetails] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [tableData, setTableData] = useState({});
  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);

  const [optTypesFilter, setOptTypesFilter] = useState([]);
  const [filterObj, setFilterObj] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [localDetails, setLocalDetails] = useState(null);

  const list = useSelector((state) => state.products.variants);
  const details = useSelector((state) => state.products.variant);
  const count = useSelector((state) => state.products.variants_count);
  const load = useSelector((state) => state.products.load);
  const productVariantsLoad = useSelector(
    (state) => state.products.variants_loading,
  );
  const branches = useSelector((state) => state.products.branches);

  const option_values = useSelector((state) => state.products.option_values);
  const option_values_loading = useSelector(
    (state) => state.products.option_values_loading,
  );

  const open = Boolean(anchorEl);

  const columns = [
    { accessor: "id", Header: "#" },
    { accessor: "variant", Header: t("products.label_variants") },
    { accessor: "sku", Header: "Sku", sort: true },
    { accessor: "quantity", Header: t("products.col_quantity"), sort: true },
    { accessor: "price", Header: t("products.col_price"), sort: true },
    {
      accessor: "price_after_discount",
      Header: t("products.col_price_after_discount"),
      sort: true,
    },
    { accessor: "location", Header: t("vendors.label_location"), sort: true },
    { accessor: "Actions", Header: t("products.col_actions") },
  ];

  const actionsList = ({ index, data }) => {
    return [
      {
        condition: () => {
          return true;
        },
        click: () => {
          getDetails(data?.id);
          setIsOpenDetails(true);
        },
        text: t("products.action_view_details"),
      },
      {
        condition: () => {
          return isSuperAdmin || variantPermissions?.can_update;
        },
        click: () => {
          if (isCreationMode) {
            setLocalDetails(data);
          } else {
            getDetails(data?.id);
          }
          setIsOpenForm(true);
        },
        text: t("products.action_edit"),
      },
      {
        condition: () => {
          return isSuperAdmin || variantPermissions?.can_delete;
        },
        click: () => {
          setOpenDeleteModal(true);
        },
        text: t("products.action_delete"),
      },
    ];
  };

  const fetchOptionValues = (filterObj, id) => {
    const requestData = {
      params: {
        option_type_id: id,
        ...filterObj,
        page_number: filterObj?.page_number || 1,
      },
    };

    dispatch(getOptionValuesRequest(requestData));
  };

  const optionTypesFilter = () => {
    if (!optionTypes) return ["dummy", "dummy"];
    const filterData = optionTypes?.map((option, i) => {
      const filterName = option?.name;
      return {
        type: "filter",
        placeholder: filterName,
        name: filterName,
        id: option?.id,
        onChange: (value) => {
          let newArr = optTypesFilter;
          newArr[i] = value?.value;
          setFilterObj((s) => {
            return {
              ...s,
              option_value_ids: `[${newArr.filter((elem) => elem)}]`,
              page_number: 1,
            };
          });
          setOptTypesFilter(newArr);
        },
        options: option_values[option?.id]?.list,
        isLoading: option_values_loading?.type === `option value ${option?.id}`,
        onMenuOpen: (filterObj) => {
          (option_values[option?.id]?.list <= 1 ||
            !option_values[option?.id]) &&
            fetchOptionValues(filterObj, option?.id);
        },
        applySearchFromRequest: true,
        count: option_values[option?.id]?.count,
        getList: (filterObj) => fetchOptionValues(filterObj, option?.id),
      };
    });
    return filterData;
  };

  const filterList = [
    {
      type: "filter",
      placeholder: t("vendors.label_location"),
      onChange: (value) => {
        setFilterObj((s) => {
          return { ...s, city_id: value?.value, page_number: 1 };
        });
      },
      options: branches,
      isLoading: load?.type === `listBranches`,
    },
    ...optionTypesFilter(),
  ];

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const handleDelete = () => {
    if (isCreationMode) {
      const newVariants = tempVariants.filter(
        (_, i) => i !== selectedRow.index,
      );
      setTempVariants(newVariants);
      toast.success(t("products.success_deleted"));
      setOpenDeleteModal(false);
      return;
    }

    const action = () => {
      //if list.length == 1 & page !== 1 -> setPage(page-1) & fetch
      if ((list.length === 1) & (page !== 1)) {
        setPage(page - 1);
        setFilterObj({
          ...filterObj,
          page_number: page - 1,
        });
        // getList({ ...filterObj, page_number: page - 1 }); //temporary until the backend sends the data correctly so I can update the state instead of re-fetching
      } else {
        // getList(filterObj);
      }
      toast.success(t("products.success_deleted"));
    };
    const requestData = {
      id: selectedRow?.rowData?.id,
      action, //dispatched on success
    };
    dispatch(deleteProductVariantRequest(requestData));
    setOpenDeleteModal(false);
  };

  //open action list
  const handleClick = (event, index, data) => {
    anchorEl === index ? setAnchorEl(null) : setAnchorEl(index);
  };

  //close action list
  const handleClose = () => {
    setSelectedRow({});
    setAnchorEl(null);
  };
  const Actions = (id, elmIndex, data) => {
    return (
      <CActions
        id={id}
        index={elmIndex}
        data={data}
        handleClick={handleClick}
        handleClose={handleClose}
        anchorRef={anchorRef}
        open={open}
        anchorEl={anchorEl}
        actionsList={actionsList}
      />
    );
  };

  const mappedData = (isCreationMode ? tempVariants : list)?.map(
    (data, index) => {
      const quantity = isCreationMode
        ? data.locations?.reduce(
            (acc, current) => acc + (Number(current.count_on_hand) || 0),
            0,
          )
        : data?.city_variant?.reduce(
            (acc, current) => acc + current.count_on_hand,
            0,
          );

      const variantDisplay = isCreationMode
        ? data.variant_name
        : data?.option_values?.map((value, i) => (
            <Typography key={i}>
              {value.name}
              {i !== data?.option_values?.length - 1 && "/"}
            </Typography>
          ));

      return {
        ...data,
        variant: (
          <Stack direction="row">
            {isCreationMode ? (
              <Typography>{variantDisplay}</Typography>
            ) : (
              variantDisplay
            )}
          </Stack>
        ),
        quantity: <Typography>{quantity}</Typography>,
        price: (
          <Typography>
            {data?.price} {data?.currency?.name || ""}
          </Typography>
        ),
        price_after_discount: (
          <Typography>
            {data?.price_after_discount} {data?.currency?.name || ""}
          </Typography>
        ),
        location: (
          <Stack direction="row">
            {isCreationMode
              ? data.locations?.map((loc, i) => (
                  <Typography key={i}>
                    {loc.city.name}
                    {i !== data.locations?.length - 1 && ", "}
                  </Typography>
                ))
              : data?.city_variant?.map((city, i) => (
                  <Typography key={i}>
                    {city?.city?.name}
                    {i !== data?.city_variant?.length - 1 && ", "}
                  </Typography>
                ))}
          </Stack>
        ),
        Actions: Actions(data?.id || index, index, data),
      };
    },
  );

  const handleOpenVariantForm = () => {
    setIsOpenForm(true);
  };

  const headerChildren = (
    <Stack
      sx={{
        position: { xs: "static", md: "absolute" },
        mr: { xs: 0, md: 5 },
      }}
    >
      <Button
        onClick={handleOpenVariantForm}
        ref={refOpenVriantForm}
        sx={{
          color: orange,
          fontWeight: "500",
          fontSize: { xs: "16px", sm: "18px", md: "20px" },
          px: 0,
        }}
      >
        {isHeaderChildren &&
          (isSuperAdmin || variantPermissions?.can_create) &&
          type === "details" &&
          t("products.action_add_new_variant")}
      </Button>
    </Stack>
  );

  const prepareVariantData = (data) => {
    const optTypes = optionTypes.map((option) => option.name);
    const option_value_ids = optTypes.map((type) => data[type]);
    let media_attributes;
    if (selectedRow?.rowData?.id) {
      const deletedImages = details?.media?.filter(
        (image) => !data?.images.includes(image.url),
      );
      const newImages = [];
      data?.images.forEach((url) => {
        const existsInObjects = details?.media?.some((obj) => obj.url === url);

        if (!existsInObjects) {
          newImages.push(url);
        }
      });
      const images =
        newImages.length > 0 ? deletedImages.concat(newImages) : deletedImages;

      media_attributes = images?.map((image) => ({
        id: image?.id,
        media_type: 0,
        url: typeof image === "object" ? image.url : image,
        _destroy: image?.id && true,
      }));
    } else {
      media_attributes = data?.images?.map((url) => {
        return {
          media_type: 0,
          url,
        };
      });
    }

    return {
      ...data,
      product_id: id,
      option_value_ids,
      media_attributes,
    };
  };

  const handleSubmit = (data, formik) => {
    if (isCreationMode) {
      const variantName = optionTypes
        .map((ot) => {
          const selectedValue = option_values[ot.id]?.list?.find(
            (v) => v.id === data[ot.name],
          );
          return selectedValue?.name || "";
        })
        .join("/");

      const newVariant = {
        ...data,
        variant_name: variantName,
        locations: [...locations],
        option_value_ids: optionTypes.map((ot) => data[ot.name]),
      };

      if (selectedRow?.rowData) {
        // Edit existing local variant
        const updatedVariants = [...tempVariants];
        updatedVariants[selectedRow.index] = newVariant;
        setTempVariants(updatedVariants);
        toast.success(t("products.success_variant_updated"));
      } else {
        // Add new local variant
        setTempVariants([...tempVariants, newVariant]);
        toast.success(t("products.success_variant_created"));
      }
      handleVariantFormClose(formik);
      return;
    }

    const action = (data) => {
      toast.success(
        selectedRow?.rowData?.id
          ? t("products.success_variant_updated")
          : t("products.success_variant_created"),
      );
      handleVariantFormClose(formik);
    };
    const requestData = {
      body: {
        variant: {
          ...prepareVariantData(data),
        },
      },
      action,
    };

    if (selectedRow?.rowData?.id) {
      dispatch(
        updateVariantRequest({ ...requestData, id: selectedRow?.rowData?.id }),
      );
    } else {
      dispatch(createVariantRequest(requestData));
    }
  };

  const handleVariantFormClose = (formik) => {
    setLocations([]);
    setIsOpenForm(false);
    setSelectedRow({});
    setLocalDetails(null);
    formik?.resetForm();
  };

  const getList = useCallback(
    async (filterObj) => {
      if (isCreationMode) return;
      const requestData = {
        params: {
          page_size: 10,
          keyword: "",
          sort_by: "id",
          product_id: id,
          ...filterObj,
          page_number: filterObj?.page_number || 1,
        },
      };
      dispatch(getProductVariantsRequest(requestData));
    },
    [isCreationMode, id, dispatch],
  );

  const getDetails = useCallback(
    async (id) => {
      if (isCreationMode) return;
      dispatch(getVariantDetailsRequest({ id: id }));
    },
    [isCreationMode, dispatch],
  );

  const handleSaveVariantBranch = (location) => {
    if (isCreationMode) {
      // In creation mode, locations are handled per-variant in the form
      // But we need to update the local 'locations' state so it reflects in the UI
      const updatedLocations = locations.map((loc) => {
        if (loc.city?.id === location.city?.id) {
          return { ...location };
        }
        return loc;
      });
      setLocations(updatedLocations);
      toast.success(t("products.success_location_saved_locally"));
      return;
    }

    const action = (data) => {
      toast.success(
        location.id
          ? t("products.success_location_updated")
          : t("products.success_location_added"),
      );
      // after adding a new branch
      // we need to update the locations with the city_id that was just created
      // just in case the user decides to delete a city right after creating it
      let updatedLocations = locations?.map((location) => {
        if (location?.city?.id === data?.data?.city_variant?.city?.id) {
          location.id = data?.data?.city_variant?.id;
        }
        return location;
      });
      setLocations(updatedLocations);
    };

    const requestData = {
      body: {
        google_cities_variant: {
          google_city_id: location?.city?.id,
          variant_id: selectedRow?.rowData?.id,
          count_on_hand: location?.count_on_hand,
        },
      },
      action,
    };
    if (!location.id) {
      //add
      dispatch(createVariantBranchRequest(requestData));
    } else {
      //update
      const requestData = {
        body: {
          count_on_hand: location?.count_on_hand,
        },
        variant_id: selectedRow?.rowData?.id,
        google_city_id: location?.city?.id,
        action,
      };
      dispatch(updateVariantBranchRequest({ ...requestData }));
    }
  };

  const handleDeleteVariantBranch = (index, location) => {
    //delete it from local state
    const newLocations = locations.filter((loc, i) => i !== index);
    setLocations(newLocations);

    const action = () => {
      // getList(filterObj);
      toast.success(t("products.success_location_deleted"));
    };
    if (location?.id) {
      //means that we are deleting a location that already exists
      const requestData = {
        variant_id: selectedRow?.rowData?.id,
        google_city_id: location?.city?.id,
        action,
      };
      dispatch(deleteVariantBranchRequest(requestData));
    }
  };

  const calculateTotalQuantity = () => {
    if (isCreationMode) {
      return tempVariants?.reduce((acc, variant) => {
        return (
          acc +
          (variant.locations?.reduce(
            (lAcc, loc) => lAcc + (Number(loc.count_on_hand) || 0),
            0,
          ) || 0)
        );
      }, 0);
    }

    // calculate total count from list in case of editing a variant count
    const arrayOfVariantsQuantities = list?.map((variant) => {
      return variant?.city_variant?.reduce(
        (acc, current) => acc + current?.count_on_hand,
        0,
      );
    });

    const totalQuantity = arrayOfVariantsQuantities?.reduce(
      (acc, current) => acc + current,
      0,
    );
    return totalQuantity;
  };

  useEffect(() => {
    if (filterObj && !isCreationMode) getList(filterObj);
  }, [filterObj, getList, isCreationMode]);

  useEffect(() => {
    rowData({ ...tableData });
  }, [tableData]);

  useEffect(() => {
    if (id && !isCreationMode) {
      setLocations(details?.city_variant || []);
    } else if (isCreationMode && selectedRow?.rowData) {
      setLocations(selectedRow.rowData.locations || []);
    }
  }, [details, selectedRow, id, isCreationMode]);

  useEffect(() => {
    if (storeId) {
      const requestData = {
        params: {
          store_id: storeId,
        },
      };
      dispatch(getVariantsBranchesRequest(requestData));
    }
  }, [storeId, dispatch]);

  if (!id && !tempVariants) {
    return (
      <Stack alignItems="center" justifyContent="center" height="400px">
        <Typography variant="h6" color="text.secondary">
          {t("products.error_save_details_first")}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ position: "relative" }}>
      {(productVariantsLoad?.type === "listVariants" ||
        productVariantsLoad?.type === "deleteVariant" ||
        load?.type === "details") && ( //because we get the optionTypes filter from the details, so we need to wait for it
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bottom: "0",
            left: "0",
            right: 0,
            top: 0,
            position: "absolute",
            zIndex: 1000,
            bgcolor: "rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(0.6px)",
          }}
        >
          <CircularProgress sx={{ color: oliveGreen }} />
        </Box>
      )}
      <Stack sx={{ position: "relative" }}>
        {type !== "details" && (
          <Stack
            sx={{
              position: "absolute",
              top: "15px",
              zIndex: 2,
              ...(i18n.language === "ar"
                ? { left: "15px" }
                : { right: "15px" }),
            }}
            direction="row"
          >
            <Typography fontWeight="400" fontSize="18px">
              {t("products.label_total_quantity")}:
            </Typography>
            <Typography fontWeight="500" fontSize="18px">
              {calculateTotalQuantity()}
            </Typography>
          </Stack>
        )}
        <ProductVariantTemplate
          isOpenForm={isOpenForm}
          isOpenDetails={isOpenDetails}
          setIsOpenDetails={setIsOpenDetails}
          details={isCreationMode ? localDetails : details}
          setOpenDeleteModal={setOpenDeleteModal}
          openDeleteModal={openDeleteModal}
          handleDelete={handleDelete}
          handleSubmit={handleSubmit}
          locations={locations}
          setLocations={setLocations}
          tableData={tableData}
          list={mappedData}
          columns={columns}
          setTableData={setTableData}
          setFilterObj={setFilterObj}
          filterList={filterList}
          headerChildren={headerChildren}
          actionsList={actionsList}
          rowData={rowData}
          handlePageChange={handlePageChange}
          page={page}
          count={isCreationMode ? tempVariants.length : count}
          optionTypesFilter={optionTypesFilter()}
          handleVariantFormClose={handleVariantFormClose}
          handleSaveVariantBranch={handleSaveVariantBranch}
          handleDeleteVariantBranch={handleDeleteVariantBranch}
          branches={branches}
          selectedRow={selectedRow}
          load={load}
          fetchOptionValues={fetchOptionValues}
          option_values={option_values}
          option_values_loading={option_values_loading}
        />
      </Stack>
    </Stack>
  );
}
